import { Alert, Box, Divider, Stack, Typography } from '@mui/material';
import React from 'react';
import type { RegistryDocument, RegistryState } from '../data/types';
import { date, units as formatUnits } from '../lib/format';

const DEFAULT_TERMS =
  'Units are held subject to the trust deed. This certificate is not a negotiable instrument; transfers are recorded on the register.';

export default function UnitCertificateView({
  state,
  doc,
}: {
  state: RegistryState;
  doc: RegistryDocument;
}) {
  const run = state.issueRuns.find((item) => item.id === doc.refId);
  const line = run?.lines.find((item) => item.holderId === doc.holderId);
  const holder = state.holders.find((item) => item.id === doc.holderId);
  const fund = state.funds.find((item) => item.id === run?.fundId);
  const unitClass = state.classes.find((item) => item.id === run?.classId);
  if (!run || !line || !holder || !fund || !unitClass)
    return <Alert severity="info">Certificate details are not available.</Alert>;
  const setup = state.fundSetups.find((item) => item.classId === unitClass.id);
  const colour = String(setup?.values['brand.colour'] ?? '#143c34');
  const title = String(setup?.values['cert.title'] ?? 'Unit Certificate');
  const terms = String(setup?.values['cert.terms'] ?? DEFAULT_TERMS);
  const name = setup?.values['inv.nameCase'] === 'UPPER' ? holder.name.toUpperCase() : holder.name;
  const partlyPaid = run.paidPerUnit < run.issuePrice;
  return (
    <Box
      sx={{
        bgcolor: '#FFFFFF',
        color: '#1E293B',
        p: { xs: 3, md: 5 },
        border: `6px double ${colour}`,
        maxWidth: 720,
        mx: 'auto',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography sx={{ fontWeight: 800, letterSpacing: '0.25em', color: colour }}>
          FIDES
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          No. {line.certificateNo}
        </Typography>
      </Stack>
      <Typography variant="h4" sx={{ textAlign: 'center', mt: 3, mb: 1, fontWeight: 700 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ textAlign: 'center', color: '#475569', mb: 3 }}>
        {fund.name} · {fund.trustee} as trustee
      </Typography>
      <Typography sx={{ textAlign: 'center', lineHeight: 1.8 }}>
        This certifies that <strong>{name}</strong> is the registered holder of{' '}
        <strong>{formatUnits(line.units)}</strong> {unitClass.name} in {fund.name},{' '}
        {partlyPaid
          ? `paid to $${run.paidPerUnit.toFixed(4)} of the $${run.issuePrice.toFixed(2)} issue price per unit`
          : 'fully paid'}
        , issued on {date(run.settlementDate)}.
      </Typography>
      <Divider sx={{ my: 3 }} />
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="caption" sx={{ color: '#64748B' }}>
            Holder number
          </Typography>
          <Typography variant="body2">{holder.accountNumber}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: '#64748B' }}>
            Issue reference
          </Typography>
          <Typography variant="body2">{run.reference}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: '#64748B' }}>
            For the trustee
          </Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            Authorised signatory
          </Typography>
        </Box>
      </Stack>
      <Typography variant="caption" component="p" sx={{ color: '#64748B', mt: 3 }}>
        {terms}
      </Typography>
    </Box>
  );
}
